import React, { useState } from 'react';
import { Screen, AppState } from '../App';
import Icon from '../components/Icon';

// A no-login product walkthrough at /demo. It shows what every feature of Decode
// My Student actually does, using the real app's UI chrome (cards, tabs, badges,
// strategy items) so it reads as a product tour — not a marketing page.
//
// Everything here is STATIC, clearly-fictional sample content: no live AI runs,
// no real student. The "Example" labelling is deliberate and must stay visible,
// and the sample copy follows the same non-clinical framing the live app uses
// (observations + who at school owns the decision; tier = level of support, the
// team decides; no diagnosis, no clinical advice).

interface Props { navigate: (s: Screen, state?: AppState) => void; }

// ---- The Talk About a Student sample -----------------------------------------
const SAMPLE = {
  initials: 'J.M.',
  grade: 'Grade 3',
  quote: 'Melts down and puts his head on the desk every time we move to independent math. Yesterday he swept his papers onto the floor.',
  summary:
    "When independent math starts, J.M. hits a wall he does not have words for. The head-down and the swept papers are almost certainly overwhelm boiling over, not defiance — his brain is reading \"do this alone\" as \"you are about to fail in front of everyone,\" and it protects him by shutting down or pushing the trigger away.",
  top3: [
    'Break the first problem into one tiny visible step and do that step with him before you walk away.',
    'Give a two-minute heads-up before the transition to independent work, so it never lands as a surprise.',
    'Offer a quiet choice: "Start with #1 or #3?" — a small sense of control lowers the threat.',
  ],
  brain:
    "A task that feels impossible to start reads to the nervous system like a threat. When J.M. sees a page he is not sure he can do alone, his stress response fires before his thinking brain gets a vote — heart rate up, focus narrowing, the prefrontal cortex (planning, starting, sequencing) going briefly offline.\n\nHead-down is the freeze response; sweeping the papers is the fight response. Both are his body trying to escape a moment that feels unsafe. He is not choosing this the way he would choose to be difficult — which is why calm co-regulation and a smaller first step work better than a consequence.",
  strategies: {
    academic: [
      'Pre-fill the first line or two so the page is never fully blank.',
      'Chunk the assignment into 3 visible steps he can check off.',
      'Let him say the first answer out loud before writing it.',
    ],
    behavioral: [
      'Name the transition early: "In two minutes we move to solo math."',
      'Set up a quiet signal he can use to ask for a check-in instead of melting down.',
      'Pre-plan a 60-second reset spot he can go to before it boils over.',
    ],
    relational: [
      'Start solo work with 30 seconds of "I\'m right here" before stepping back.',
      'Catch and name one thing he started well, out loud, every day this week.',
      'Debrief a rough moment later, calmly, so he knows the relationship held.',
    ],
  },
  forYou:
    "This one is genuinely hard. Watching a child come apart during a subject you have to keep moving through — while twenty other students need you — is exhausting, and it is okay to feel worn down by it. You are not doing this wrong. Noticing the pattern instead of just reacting to it is the hard part, and you are already there.",
  mtssTier: 2,
  mtssIndicators: [
    'A predictable, repeating trigger (independent math) rather than a one-off.',
    'Escalation that now includes throwing materials, not only withdrawal.',
    'Universal (Tier 1) supports in place but not yet enough on their own.',
  ],
  mtssExplanation:
    "These observations are the kind a VTmtss team would most likely discuss at a targeted (Tier 2) level of support — a repeating, predictable pattern that classroom-wide strategies alone are not resolving. This is preparation for that team conversation, not a determination about J.M.",
  referral: [
    'The specific trigger and what precedes it (moving to independent math).',
    'What the behavior looks like and how it has escalated over ~3 weeks.',
    'Which classroom strategies you have tried and how J.M. responded.',
  ],
};

// Parent demo — same fictional shape, retargeted to a parent at home. Replaces
// the tier with askSchool ("What to ask the school for").
const PARENT_SAMPLE = {
  initials: 'Marcus',
  grade: 'Age 8',
  quote: 'Melts down every night at homework time — yelling, throwing his pencil, then shutting down. Bedtime has gotten hard too.',
  summary:
    "When homework starts, Marcus hits a wall he can't put into words. The yelling and the shutdown are almost certainly overwhelm spilling over, not him being difficult — his brain reads \"do this hard thing now\" as a threat after a long day of holding it together at school, and it protects him by pushing back or going quiet.",
  top3: [
    "Tonight, shrink the first step: do just the first problem together, out loud, before stepping back.",
    "Give a calm 5-minute heads-up before homework starts so it never lands as a surprise.",
    "Try saying: \"This looks really hard. You don't have to do it alone — let's take one small piece together.\"",
  ],
  brain:
    "After a full day of managing himself at school, Marcus comes home with an empty tank. Homework asks his thinking brain to start something hard exactly when it has the least left — so his stress response fires first: frustration up, focus narrowing, the part of the brain that plans and starts going briefly offline.\n\nThe yelling is his fight response; the shutdown is his freeze response. Both are his body trying to escape a moment that feels impossible, not a choice to defy you. That's why a calm voice and a smaller first step land better than pushing harder.",
  strategies: {
    academic: [
      'Break homework into 2–3 tiny chunks with a short break between each.',
      'Sit nearby for the first minute so starting feels less lonely.',
      'Let him say an answer out loud before writing it.',
    ],
    behavioral: [
      'Set a predictable homework time and give a 5-minute warning before it.',
      'Agree on a quiet signal he can use when he needs a short break.',
      'Keep a calm "reset spot" he can go to before it boils over.',
    ],
    relational: [
      'Start with 30 seconds of connection before homework, not a reminder about it.',
      'Notice one thing he did well each night, out loud.',
      'Circle back calmly after a hard moment so he knows you two are okay.',
    ],
  },
  forYou:
    "This is genuinely exhausting. Ending every day in a battle over homework — when you just want to help him — wears anyone down, and it does not mean you are getting it wrong. Noticing the pattern instead of just reacting to it is the hard part, and you are already doing it.",
  askSchool: [
    'Is the amount of homework right for where Marcus is right now?',
    'Could he have a quick check-in with the teacher at the start or end of the day?',
    'Are there small supports in class that could lower his stress before he gets home?',
    'Who on the school team can we loop in if this keeps up?',
  ],
  askWhy:
    "These are plain questions to raise together — the school's team decides what support a child receives, and you know your child best.",
  referral: [
    'The specific pattern and when it happens (every night, at homework time).',
    'How it shows up and how long it has been going on.',
    'What you have already tried at home and how Marcus responded.',
  ],
};

type Tab = 'summary' | 'brain' | 'strategies' | 'forYou' | 'mtss' | 'refer';
const TEACHER_TABS: [Tab, string][] = [
  ['summary', 'Summary'], ['brain', 'Brain Science'], ['strategies', 'Strategies'],
  ['forYou', 'For You'], ['mtss', 'MTSS Tier'], ['refer', 'Referral'],
];
const PARENT_TABS: [Tab, string][] = [
  ['summary', 'Summary'], ['brain', 'Brain Science'], ['strategies', 'Strategies'],
  ['forYou', 'For You'], ['mtss', 'What to ask'], ['refer', 'Outside help'],
];

// ---- The rest of the toolkit -------------------------------------------------
const FEATURES = [
  {
    emoji: '🚨',
    title: 'Urgency Mode',
    tag: 'In seconds',
    desc: 'When it is happening right now, get one calm, immediately usable classroom strategy in seconds — no forms, no reading.',
  },
  {
    emoji: '💬',
    title: 'Communication Helper',
    tag: 'Parent & colleague messages',
    desc: 'Drafts a warm, professional parent message, plus what to say if the conversation gets hard and what to document after.',
  },
  {
    emoji: '📚',
    title: 'Brain Science Library',
    tag: 'Self-paced learning',
    desc: 'Plain-language explainers on the patterns you see most — meltdowns, shutdown, attention, anxiety, trauma responses, and more.',
  },
  {
    emoji: '🗂️',
    title: 'Student Profiles',
    tag: 'Gets smarter over time',
    desc: 'Every conversation about a student is saved (initials only) so guidance builds on what you already tried, instead of starting over.',
  },
  {
    emoji: '✅',
    title: 'Strategy Tracker',
    tag: 'Checks back in 3 days',
    desc: 'Mark a strategy as "trying this today" and the app checks back in three days to see what happened — and suggests the next move.',
  },
  {
    emoji: '📝',
    title: 'Substitute Teacher Brief',
    tag: 'Ready in 60 seconds',
    desc: 'One tap turns what you know about a student into a calm, practical brief a sub can read in a minute and act on.',
  },
  {
    emoji: '🌱',
    title: 'Burnout Tracker',
    tag: 'Your wellbeing counts',
    desc: 'A quick, private check-in on how YOU are doing, with gentle support when the week is grinding you down.',
  },
  {
    emoji: '🌤️',
    title: 'Friday Debrief',
    tag: 'Close out the week',
    desc: 'A short, guided reflection to name what went well, what is unresolved, and what to let go of before the weekend.',
  },
  {
    emoji: '🏆',
    title: 'Wins Board',
    tag: 'The moments that matter',
    desc: 'A running collection of the small breakthroughs — the ones easy to forget by June and worth keeping.',
  },
];

const LIBRARY_TOPICS = [
  'Explosive Behavior & Meltdowns', 'Shutdown & Withdrawal', 'Attention & Focus',
  'Anxiety in the Classroom', 'Trauma Responses', 'Oppositional Behavior',
  'Sensory Sensitivities', 'Grief & Loss', 'Learning Differences',
];

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.6, color: 'var(--teal)', marginBottom: 8, textTransform: 'uppercase' }}>{children}</p>
);

const ExampleBanner = () => (
  <div style={{ background: 'var(--amber-light)', color: '#b87800', padding: '8px 16px', fontSize: 12, fontWeight: 700, letterSpacing: 0.4, display: 'flex', alignItems: 'center', gap: 8 }}>
    <Icon name="sparkle" size={15} /> EXAMPLE — not a real student or a live AI result
  </div>
);

export default function Demo({ navigate }: Props) {
  const [tab, setTab] = useState<Tab>('summary');
  const [view, setView] = useState<'teacher' | 'parent'>('teacher');
  const parent = view === 'parent';
  const S: any = parent ? PARENT_SAMPLE : SAMPLE;
  const TABS = parent ? PARENT_TABS : TEACHER_TABS;
  const cta = () => navigate('signup', { accountType: view });

  return (
    <div style={{ paddingBottom: 0 }}>
      {/* Header */}
      <div style={{ background: 'white', boxShadow: 'var(--shadow-sm)', position: 'sticky', top: 0, zIndex: 100 }}>
        <div className="mkt-inner" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px' }}>
          <button onClick={() => navigate('landing')} style={{ display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <img src="/logo.png" alt="Decode My Student" style={{ height: 40, width: 40, borderRadius: 9, marginRight: 10 }} />
            <span style={{ fontFamily: 'Playfair Display', fontSize: 18, fontWeight: 700, color: 'var(--teal)' }}>Decode My Student</span>
          </button>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <button onClick={() => navigate('login')} style={{ background: 'none', color: 'var(--teal)', fontWeight: 600, fontSize: 14, padding: '8px 12px', border: 'none', cursor: 'pointer' }}>Log In</button>
            <button onClick={cta} className="btn-primary" style={{ width: 'auto', padding: '9px 18px', fontSize: 14 }}>Start Free Trial</button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="landing-hero">
        <div className="mkt-hero-inner" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <div className="hero-badge">Live Product Walkthrough · No Login</div>
          <h1 className="mkt-h1" style={{ color: 'white', marginBottom: 16 }}>See exactly what a teacher gets.</h1>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.85)', marginBottom: 28, lineHeight: 1.6 }}>
            This is a real tour of Decode My Student — every feature, with genuine sample output. Start with the core tool below: describe a student, and see the full response a teacher receives.
          </p>
          <div style={{ maxWidth: 360, margin: '0 auto' }}>
            <button onClick={cta} style={{ background: 'var(--amber)', color: 'white', padding: '15px 32px', borderRadius: 12, fontSize: 16, fontWeight: 700, width: '100%', border: 'none', cursor: 'pointer' }}>Start Your Free 7-Day Trial</button>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13, marginTop: 10 }}>No credit card required · Full access from day one</p>
          </div>
        </div>
      </div>

      {/* Teacher / Parent demo toggle */}
      <div className="mkt-inner" style={{ maxWidth: 720, padding: '28px 24px 0', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', background: '#f0ece6', borderRadius: 12, padding: 4, gap: 2 }}>
          <button onClick={() => { setView('teacher'); setTab('summary'); }} className={`tab${!parent ? ' active' : ''}`} style={{ padding: '10px 20px', flex: 'none' }}>👩‍🏫 Teacher demo</button>
          <button onClick={() => { setView('parent'); setTab('summary'); }} className={`tab${parent ? ' active' : ''}`} style={{ padding: '10px 20px', flex: 'none' }}>👪 Parent demo</button>
        </div>
      </div>

      {/* CORE: Talk About a Student / My Child */}
      <div className="mkt-inner" style={{ maxWidth: 720, padding: '24px 24px 8px' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <span className="badge badge-teal" style={{ marginBottom: 12, display: 'inline-block' }}>THE CORE FEATURE</span>
          <h2 style={{ fontSize: 26, marginBottom: 8 }}>{parent ? 'Talk About My Child' : 'Talk About a Student'}</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6 }}>
            Describe any struggle in your own words — by voice or text. Here is the complete response, exactly as it appears in the app. Tap the tabs to explore it.
          </p>
        </div>

        {/* Input card */}
        <div className="card" style={{ marginBottom: 16, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          <div className="mic-btn" style={{ width: 44, height: 44 }} aria-hidden>
            <Icon name="mic" size={18} style={{ color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span className="mono" style={{ fontSize: 18, fontWeight: 500, color: 'var(--teal)' }}>{S.initials}</span>
              <span className="badge badge-teal">{S.grade}</span>
            </div>
            <p style={{ fontSize: 15, color: 'var(--charcoal)', fontStyle: 'italic', lineHeight: 1.6 }}>“{S.quote}”</p>
          </div>
        </div>

        {/* Result card — real UI chrome */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <ExampleBanner />
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span className="mono" style={{ fontSize: 22, color: 'var(--teal)', fontWeight: 500 }}>{S.initials}</span>
              <span className="badge badge-teal">{S.grade}</span>
              {!parent && <span className="badge badge-amber">MTSS Tier {S.mtssTier}</span>}
            </div>

            <div className="tabs">
              {TABS.map(([k, l]) => (
                <button key={k} className={`tab${tab === k ? ' active' : ''}`} onClick={() => setTab(k)}>{l}</button>
              ))}
            </div>

            {tab === 'summary' && (
              <div className="animate-in">
                <div className="teal-card" style={{ marginBottom: 16 }}>
                  <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 10, fontSize: 15 }}>Here's what's likely happening with {S.initials}</p>
                  <p style={{ fontSize: 15, lineHeight: 1.7 }}>{S.summary}</p>
                </div>
                <p style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Top 3 Things To Try Today</p>
                {S.top3.map((s: string, i: number) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <div className={`strategy-item${i === 0 ? ' highlight' : ''}`} style={{ marginBottom: 0, borderRadius: '8px 8px 0 0' }}>
                      <span style={{ fontWeight: 700, marginRight: 8 }}>{i + 1}.</span>{s}
                    </div>
                    <div style={{ background: 'var(--teal-light)', padding: '8px 14px', borderRadius: '0 0 8px 8px', fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>
                      ✋ I'm trying this today — remind me to check in
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === 'brain' && (
              <div className="animate-in">
                <p style={{ fontWeight: 700, marginBottom: 12, color: 'var(--teal)' }}>The Brain Science</p>
                <p style={{ fontSize: 15, lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{S.brain}</p>
              </div>
            )}

            {tab === 'strategies' && (
              <div className="animate-in">
                {([['academic', '📚 Academic'], ['behavioral', '🎯 Behavioral'], ['relational', '💛 Relational']] as const).map(([key, label]) => (
                  <div key={key} style={{ marginBottom: 20 }}>
                    <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>{label} Strategies</p>
                    {S.strategies[key].map((s: string, i: number) => (
                      <div key={i} className={`strategy-item${i === 0 ? ' highlight' : ''}`}>
                        {i === 0 && <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.25)', padding: '2px 6px', borderRadius: 8, marginRight: 6 }}>TRY TODAY</span>}
                        {s}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {tab === 'forYou' && (
              <div className="animate-in">
                <div style={{ background: 'var(--green-light)', borderRadius: 12, padding: '20px', marginBottom: 16 }}>
                  <p style={{ fontWeight: 700, color: 'var(--green)', marginBottom: 8 }}>💚 This section is just for you.</p>
                  <p style={{ fontSize: 14, lineHeight: 1.8 }}>{S.forYou}</p>
                </div>
                <div style={{ background: 'var(--amber-light)', borderRadius: 10, padding: '16px', textAlign: 'center' }}>
                  <p style={{ fontWeight: 700, color: '#b87800' }}>You are not alone in this.</p>
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>What you are doing matters more than you know.</p>
                </div>
              </div>
            )}

            {tab === 'mtss' && parent && (
              <div className="animate-in">
                <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, lineHeight: 1.6, color: 'var(--teal)' }}>
                  <strong>How to use this:</strong> questions you can bring to the school — the school's team decides what support your child receives.
                </div>
                <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 15 }}>What to ask the school for</p>
                {S.askSchool.map((q: string, i: number) => (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 14, display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--teal)', fontWeight: 700 }}>•</span>{q}
                  </div>
                ))}
                {S.askWhy && <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 12, fontStyle: 'italic' }}>{S.askWhy}</p>}
              </div>
            )}

            {tab === 'mtss' && !parent && (
              <div className="animate-in">
                <div style={{ background: 'var(--amber-light)', borderRadius: 14, padding: '20px', marginBottom: 20, textAlign: 'center' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b87800', marginBottom: 6 }}>MTSS Assessment</p>
                  <p style={{ fontSize: 40, fontWeight: 800, color: 'var(--amber)', lineHeight: 1 }}>Tier {S.mtssTier}</p>
                  <p style={{ fontSize: 13, marginTop: 8, color: 'var(--text-secondary)' }}>Targeted Support</p>
                </div>
                <div style={{ marginBottom: 20 }}>
                  {[{ tier: 3, label: 'Tier 3 — Intensive', pct: '20%', color: 'var(--coral)' }, { tier: 2, label: 'Tier 2 — Targeted', pct: '60%', color: 'var(--amber)' }, { tier: 1, label: 'Tier 1 — Universal', pct: '100%', color: 'var(--green)' }].map(t => (
                    <div key={t.tier} style={{ width: t.pct, margin: '0 auto 4px', background: t.tier === S.mtssTier ? t.color : '#e0dbd3', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: t.tier === S.mtssTier ? 'white' : 'var(--text-secondary)' }}>{t.label}</p>
                    </div>
                  ))}
                </div>
                <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>How this lines up for the team</p>
                <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 16, color: 'var(--text-secondary)' }}>{S.mtssExplanation}</p>
                <p style={{ fontWeight: 700, marginBottom: 8, fontSize: 15 }}>What a team would weigh</p>
                {S.mtssIndicators.map((ind: string, i: number) => (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 14, display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--amber)', fontWeight: 700 }}>•</span>{ind}
                  </div>
                ))}
                <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px', marginTop: 16, border: '1px solid var(--teal)' }}>
                  <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>Bring this to your school's team</p>
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>Tier 2 and Tier 3 supports are decided by your school's student support / MTSS team. Share these observations with your school counselor or student support team.</p>
                </div>
                <p style={{ fontSize: 12, lineHeight: 1.6, color: 'var(--text-secondary)', marginTop: 16, fontStyle: 'italic' }}>
                  Framed using the Vermont Multi-tiered System of Supports (VTmtss) Field Guide (2019) and 16 V.S.A. § 2902. The tier of support a student receives is decided by the school's team, not by this tool.
                </p>
              </div>
            )}

            {tab === 'refer' && (
              <div className="animate-in">
                <p style={{ fontWeight: 700, marginBottom: 12, color: 'var(--teal)' }}>{parent ? 'When to get outside help' : "What's worth documenting"}</p>
                <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 12, color: 'var(--text-secondary)' }}>
                  {parent
                    ? `Rather than deciding whether ${S.initials} needs help, the app lists the concrete things worth keeping an eye on and names who to talk to.`
                    : `Rather than deciding whether ${S.initials} needs outside help, the app lists the concrete, observable things worth writing down and names who at school owns the decision.`}
                </p>
                {S.referral.map((r: string, i: number) => (
                  <div key={i} style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 14px', marginBottom: 8, fontSize: 14, display: 'flex', gap: 8 }}>
                    <span style={{ color: 'var(--teal)', fontWeight: 700 }}>•</span>{r}
                  </div>
                ))}
                <div style={{ background: 'var(--teal-light)', borderRadius: 12, padding: '16px', border: '1px solid var(--teal)', marginTop: 12 }}>
                  <p style={{ fontWeight: 700, color: 'var(--teal)', marginBottom: 6 }}>{parent ? 'Who to talk to' : 'Who to loop in at school'}</p>
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>{parent ? "Bring these to your child's teacher or school counselor, and your pediatrician or family doctor. They can help you decide together what, if anything, comes next. This tool does not diagnose or give medical advice." : "Decisions about additional support belong to your school's team. Share what you documented with your school counselor or student support team, or ask your administrator about next steps."}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Point-of-use disclaimer, matching the live app */}
        <p style={{ fontSize: 11.5, lineHeight: 1.6, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 20, padding: '0 12px' }}>
          Decode My Student is a professional development tool. It does not diagnose, treat, or provide clinical advice about any child.
        </p>
      </div>

      {/* Urgency Mode spotlight */}
      <div style={{ padding: '40px 0 8px' }}>
        <div className="mkt-inner" style={{ maxWidth: 720 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span className="badge badge-coral" style={{ marginBottom: 12, display: 'inline-block' }}>WHEN IT'S HAPPENING NOW</span>
            <h2 style={{ fontSize: 26, marginBottom: 8 }}>Urgency Mode</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6 }}>One calm, usable move in seconds — no forms, no reading. Here's the kind of response you get.</p>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <ExampleBanner />
            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: 14, color: 'var(--charcoal)', fontStyle: 'italic', marginBottom: 16 }}>
                “A student is escalating fast over a frustrating assignment — voice rising, about to lose it.”
              </p>
              <div style={{ background: 'var(--coral-light)', borderLeft: '4px solid var(--coral)', borderRadius: '0 10px 10px 0', padding: '16px 18px' }}>
                <p style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--charcoal)' }}>
                  Right now, lower your voice below theirs and get to eye level without crowding them. Say: “This got really hard, really fast. You don't have to do it alone — let's take one small step together.” Then pause and let the silence do some of the work.
                </p>
              </div>
              <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '14px 16px', marginTop: 14, fontSize: 13, color: 'var(--teal)', lineHeight: 1.6 }}>
                <strong>Safety first:</strong> if what you describe involves harm, danger, or crisis, Urgency Mode does not generate advice — it routes you straight to your school's crisis protocol, 988, and 911. This tool is for classroom moments, not emergencies.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* The rest of the toolkit */}
      <div style={{ padding: '40px 0' }}>
        <div className="mkt-inner">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <h2 style={{ fontSize: 26, marginBottom: 8 }}>Everything else in the toolkit</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, maxWidth: 560, margin: '0 auto' }}>
              Decode My Student is built for the whole week — the student in front of you, and the teacher behind the desk.
            </p>
          </div>
          <div className="mkt-features">
            {FEATURES.map(f => (
              <div key={f.title} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 24 }}>{f.emoji}</span>
                    <p style={{ fontWeight: 700, fontSize: 15 }}>{f.title}</p>
                  </div>
                  <span className="badge badge-teal" style={{ whiteSpace: 'nowrap' }}>{f.tag}</span>
                </div>
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Communication Helper sample */}
      <div style={{ padding: '8px 0 8px' }}>
        <div className="mkt-inner" style={{ maxWidth: 720 }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <h2 style={{ fontSize: 24, marginBottom: 8 }}>A closer look: Communication Helper</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6 }}>Tell it the situation; it drafts the message and prepares you for the conversation.</p>
          </div>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <ExampleBanner />
            <div style={{ padding: '20px' }}>
              <SectionLabel>What to say</SectionLabel>
              <div style={{ background: 'var(--teal-light)', borderRadius: 10, padding: '14px 16px', marginBottom: 18, fontSize: 14, lineHeight: 1.7 }}>
                “Hi Ms. Rivera — thank you for partnering with me on J.'s year. I wanted to share something I'm noticing and get your read on it. During independent math, J. tends to get overwhelmed and shut down. I'm trying a few small supports at school, and I'd love to hear what you see at home so we can line up. Would a quick call this week work?”
              </div>
              <SectionLabel>If it gets hard</SectionLabel>
              <p style={{ fontSize: 14, lineHeight: 1.7, marginBottom: 18, color: 'var(--charcoal)' }}>
                If the parent gets defensive: “I hear you — I want to make sure we're on the same team here. We both want J. to feel successful.” Keep it about shared goals, not blame.
              </p>
              <SectionLabel>After the conversation</SectionLabel>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--charcoal)' }}>
                Document the date, what you agreed on, and one next step. Send a brief follow-up so nothing gets lost.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Brain Science Library topics */}
      <div style={{ padding: '32px 0' }}>
        <div className="mkt-inner" style={{ maxWidth: 720, textAlign: 'center' }}>
          <h2 style={{ fontSize: 24, marginBottom: 8 }}>A closer look: Brain Science Library</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.6, marginBottom: 20 }}>Plain-language explainers on the patterns you see most — read one in a few minutes.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
            {LIBRARY_TOPICS.map(t => (
              <span key={t} style={{ background: 'white', boxShadow: 'var(--shadow-sm)', color: 'var(--teal)', padding: '8px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Closing CTA */}
      <div style={{ background: 'var(--teal-dark)', color: 'white', padding: '56px 24px' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ color: 'white', marginBottom: 12, fontSize: 26 }}>Ready to try it with your own students?</h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: 24, fontSize: 15, lineHeight: 1.6 }}>
            Everything you just saw runs live the moment you sign up. Start free — no credit card, full access for 7 days.
          </p>
          <div style={{ maxWidth: 360, margin: '0 auto' }}>
            <button onClick={cta} style={{ background: 'var(--amber)', color: 'white', padding: '16px 32px', borderRadius: 12, fontSize: 16, fontWeight: 700, width: '100%', border: 'none', cursor: 'pointer', marginBottom: 10 }}>Start Your Free 7-Day Trial</button>
            <button onClick={() => navigate('login')} style={{ background: 'rgba(255,255,255,0.12)', color: 'white', padding: '12px 24px', borderRadius: 10, fontSize: 15, fontWeight: 600, width: '100%', border: '1px solid rgba(255,255,255,0.35)', cursor: 'pointer' }}>I already have an account</button>
          </div>
          <p style={{ marginTop: 16, fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>Built for teachers. Grounded in brain science.</p>
        </div>
      </div>
    </div>
  );
}
